/**
 * Punto único para generar el PDF de resultados del laboratorio.
 * Delega al backend Node → Python (ReportBro / WeasyPrint; Word opcional).
 */
import axios from 'axios';
import { API_URL } from '../config';
import { FEATURE_FLAG_DEFAULTS } from '../constants/featureFlags';
import { resolveFormatoResultados } from './formatosApi';
import { prepararOrdenParaPdf } from './prepararOrdenParaPdf';

/** Verde oficial del laboratorio (portal y vistas previas). */
export const ACCENT_COLOR_VERDE_OFICIAL = '#2e7d32';

export interface OpcionesPdfResultados {
  usuarioFirma?: { nombre: string; titulo?: string | null; codigo_minsa?: string | null };
  formatDate?: (d: string) => string;
  fallbackAccentColor?: string;
  forceAccentColor?: string;
  isPortal?: boolean;
  mostrarSellos?: boolean;
  forzarFormatoUnico?: boolean;
  authToken?: string | null;
  /** Si true (default en impresión staff), regenera el PDF sin usar caché en disco. */
  forceRefresh?: boolean;
}

export type CrearPdfResultadosParams = {
  orden: any;
  config: any;
  formatoResultados?: any | null;
  options?: OpcionesPdfResultados;
};

export type PdfResultadoLaboratorio = { kind: 'blob'; blob: Blob; filename: string };

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

let flagsCache: { flags: Record<string, boolean>; ts: number } | null = null;
const FLAGS_TTL_MS = 30_000;

async function obtenerFeatureFlags(): Promise<Record<string, boolean>> {
  const now = Date.now();
  if (flagsCache && now - flagsCache.ts < FLAGS_TTL_MS) {
    return flagsCache.flags;
  }
  try {
    const res = await axios.get(`${API_URL}/configuracion/features`, { headers: authHeaders() });
    const flags = { ...FEATURE_FLAG_DEFAULTS, ...(res.data?.flags || {}) };
    flagsCache = { flags, ts: now };
    return flags;
  } catch {
    return { ...FEATURE_FLAG_DEFAULTS };
  }
}

async function plantillasWordHabilitadas(): Promise<boolean> {
  const flags = await obtenerFeatureFlags();
  return !!flags.usar_plantillas_word;
}

export async function resolverFormatoResultadosParaOrden(orden: any): Promise<any | null> {
  const primer = orden?.examenes?.[0];
  const resolved = await resolveFormatoResultados({
    examenId: primer?.id ?? primer?.examen_id ?? null,
    categoriaId: primer?.categoria_id ?? null,
  });
  return resolved?.formato ?? null;
}

function nombreArchivoResultados(orden: any): string {
  const num = orden?.numero_orden ?? orden?.id ?? 'orden';
  const paciente = String(orden?.paciente_nombre ?? 'paciente').replace(/\s+/g, '_');
  return `Resultados_${num}_${paciente}.pdf`;
}

function validarSaldoPendiente(orden: any): void {
  if (Number(orden?.factura_saldo_pendiente ?? 0) > 0) {
    throw new Error('Pendiente de Pago');
  }
}

async function crearPdfWordDesdeBackend(
  ordenId: number,
  options?: OpcionesPdfResultados
): Promise<PdfResultadoLaboratorio> {
  const res = await axios.post(
    `${API_URL}/ordenes/${ordenId}/pdf-word`,
    { usuarioFirma: options?.usuarioFirma ?? null },
    { headers: authHeaders(), responseType: 'blob' }
  );
  return { kind: 'blob', blob: res.data as Blob, filename: '' };
}

/** ReportBro → SQLite → Python WeasyPrint (`/api/pdf/generar-resultado-reportbro`). */
async function crearPdfHtmlDesdeBackend(
  ordenId: number,
  options?: OpcionesPdfResultados
): Promise<PdfResultadoLaboratorio> {
  const res = await axios.post(
    `${API_URL}/ordenes/${ordenId}/pdf-html`,
    {
      usuarioFirma: options?.usuarioFirma ?? null,
      // Por defecto usa caché (fingerprint de resultados). Solo regenerar si se pide explícito.
      forceRefresh: options?.forceRefresh === true,
    },
    { headers: authHeaders(), responseType: 'blob', validateStatus: () => true }
  );
  if (res.status >= 400) {
    throw Object.assign(new Error(await extraerMensajeErrorBlob(res.data, res.status)), {
      response: res,
    });
  }
  return { kind: 'blob', blob: res.data as Blob, filename: '' };
}

async function extraerMensajeErrorBlob(data: unknown, status?: number): Promise<string> {
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const json = JSON.parse(text) as { error?: string; detail?: string };
        if (json.error) return String(json.error);
        if (json.detail) return String(json.detail);
      } catch {
        /* no JSON */
      }
      if (text.trim()) return text.slice(0, 400);
    } catch {
      /* ignore */
    }
  } else if (data && typeof data === 'object' && 'error' in data) {
    return String((data as { error?: string }).error || '');
  }
  if (status === 503) {
    return 'El servicio PDF no está disponible. Inicie Clinica-Python-PDF (puerto 8765): npm run pm2:start o npm run dev:python';
  }
  if (status === 500) {
    return 'No se pudo generar el PDF. Verifique que el servicio Python PDF esté activo en el puerto 8765.';
  }
  return `Error al generar PDF (${status ?? 'desconocido'})`;
}

/**
 * Portal público: PDF vía backend sin JWT de staff.
 */
async function crearPdfPortalDesdeBackend(token: string, orden: any): Promise<PdfResultadoLaboratorio> {
  validarSaldoPendiente(orden);
  const res = await axios.get(`${API_URL}/portal/resultados/${encodeURIComponent(token)}/pdf`, {
    responseType: 'blob',
  });
  return { kind: 'blob', blob: res.data as Blob, filename: nombreArchivoResultados(orden) };
}

/**
 * Genera el PDF oficial de resultados vía servicio Python.
 * Resuelve formato en el servidor (`formatos/resolve/resultados`) y agrupa por categoría.
 */
export async function crearPdfResultadosLaboratorio(
  params: CrearPdfResultadosParams & { portalToken?: string }
): Promise<PdfResultadoLaboratorio> {
  const orden = prepararOrdenParaPdf(params.orden);
  validarSaldoPendiente(orden);

  const ordenId = orden?.id ?? orden?.orden_id;

  if (params.portalToken) {
    return crearPdfPortalDesdeBackend(params.portalToken, orden);
  }

  if (!ordenId) {
    throw new Error('Se requiere orden.id para generar el PDF de resultados.');
  }

  const tieneToken = !!localStorage.getItem('token');
  if (!tieneToken) {
    throw new Error('Sesión requerida para generar el PDF de resultados.');
  }

  // Prioridad: layout absoluto (json_layout / WeasyPrint) vía pdf-html del backend
  try {
    const pdf = await crearPdfHtmlDesdeBackend(ordenId, params.options);
    return { ...pdf, filename: nombreArchivoResultados(orden) };
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 403 && String(e?.response?.data?.error || e.message).includes('Pendiente')) {
      throw new Error('Pendiente de Pago');
    }
    if (await plantillasWordHabilitadas()) {
      try {
        const pdf = await crearPdfWordDesdeBackend(ordenId, params.options);
        return { ...pdf, filename: nombreArchivoResultados(orden) };
      } catch (wordErr: any) {
        throw wordErr;
      }
    }
    if (status === 403 || status === 404 || status === 503) {
      throw new Error(
        (await extraerMensajeErrorBlob(e?.response?.data, status)) ||
          e?.response?.data?.error ||
          'No se pudo generar el PDF. Configure un formato con json_layout en Diseño de reportes.'
      );
    }
    if (status === 500) {
      throw new Error(await extraerMensajeErrorBlob(e?.response?.data, 500));
    }
    throw e;
  }
}

export function guardarPdfResultado(result: PdfResultadoLaboratorio, filename?: string): void {
  const name = filename ?? result.filename ?? 'resultados.pdf';
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export async function pdfResultadoToDataUri(result: PdfResultadoLaboratorio): Promise<string> {
  const buf = await result.blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return `data:application/pdf;base64,${btoa(binary)}`;
}

function tituloSeguroPdf(texto: string): string {
  return String(texto || 'Resultados').replace(/[<>&"']/g, '');
}

/** Abre el diálogo de impresión del PDF (sin chrome del visor). */
export async function abrirPdfResultadoEnVentana(
  result: PdfResultadoLaboratorio,
  titulo = 'Resultados'
): Promise<void> {
  if (!result.blob || result.blob.size === 0) {
    throw new Error('El PDF generado está vacío.');
  }
  const { imprimirPdfBlob } = await import('./pdfBlobHelpers');
  const filename = result.filename || `${tituloSeguroPdf(titulo)}.pdf`;
  await imprimirPdfBlob(result.blob, filename);
}

/** @deprecated Todos los PDFs de resultados son blob del servidor. */
export function esPdfJs(_result: PdfResultadoLaboratorio): false {
  return false;
}

/** @deprecated Use guardarPdfResultado o abrirPdfResultadoEnVentana. */
export function obtenerJsPdf(): never {
  throw new Error('Los PDFs de resultados se generan en el servidor (WeasyPrint/Word).');
}
